// ErrorBoundary global — catch les erreurs descendantes, log (PostHog en prod,
// console.error en dev), fallback UI "Oops, something broke".
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { posthog } from "@/lib/posthog";

/**
 * Redact PII / tokens des error messages avant envoi PostHog.
 * Couvre : JWT, Bearer tokens, API keys, emails, URLs avec query tokens.
 */
function redactPii(text: string): string {
  return text
    .replace(/eyJ[A-Za-z0-9._-]{20,}/g, "[REDACTED_JWT]")
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [REDACTED]")
    .replace(/sk_(?:live|test)_[A-Za-z0-9]+/g, "[REDACTED_STRIPE_KEY]")
    .replace(/phc_[A-Za-z0-9]+/g, "[REDACTED_POSTHOG_KEY]")
    .replace(/[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[REDACTED_EMAIL]")
    .replace(/(access_token|refresh_token|api_key)=[^&\s]+/gi, "$1=[REDACTED]");
}

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
    if (typeof posthog.capture === "function") {
      try {
        // Redact PII avant envoi PostHog (OWASP ASVS V7.1.1) :
        // - JWT (eyJ...)
        // - Bearer tokens
        // - emails (basic regex)
        posthog.capture("frontend_error", {
          message: redactPii(error.message),
          stack: redactPii(error.stack ?? ""),
          componentStack: redactPii(info.componentStack ?? ""),
        });
      } catch {
        /* posthog not init */
      }
    }
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg p-6 text-ink">
          <div className="max-w-md space-y-4 text-center">
            <div className="flex justify-center">
              <AlertTriangle className="h-10 w-10 text-warning" />
            </div>
            <h1 className="text-xl font-semibold text-ink-strong">Something went wrong</h1>
            <p className="text-sm text-ink-soft">
              We've logged the error. Try reloading the page, or contact support if the issue persists.
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="primary" onClick={() => window.location.reload()}>
                Reload
              </Button>
              <Button variant="outline" onClick={() => this.setState({ error: null })}>
                Dismiss
              </Button>
            </div>
            {import.meta.env.DEV ? (
              <pre className="mt-4 max-h-40 overflow-auto rounded border border-border bg-surface p-2 text-left text-xs text-ink-faint">
                {this.state.error.stack}
              </pre>
            ) : null}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
